/*
 *  Copyright 2026 LiteFarm.org
 *  This file is part of LiteFarm.
 *
 *  LiteFarm is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  LiteFarm is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 *  GNU General Public License for more details, see <https://www.gnu.org/licenses/>.
 */

/**
 * buildAnimalPayloads — Phase 8 / Plan 08-05 (UI-07, LD-20).
 *
 * Pure helpers that map slice page entities (`scanPassportSlice` review queue)
 * into the wire-shapes consumed by:
 *   - `POST /animals`  (LD-20 — strict array body — handled by animalController.addAnimals)
 *   - `PATCH /animals` (D-4 / R-4 — strict array body — animalController.editAnimals)
 *
 * Per-entity precedence: `entity.formValues.X` overrides `entity.X` (UI-04 — user
 * edits in the review form win over OCR-mapped values).
 *
 * UI-internal fields are stripped — they MUST NEVER appear in the API payload:
 *   pageIndex, skipped, mode, target_animal_id, formValues, duplicate, saved,
 *   _unrecoverable_fields, _mapping_inputs, _mapping_error, _source_file_index.
 *
 * MED-4 V-PATCH-FIELDS (silent-data-loss prevention) — executed 2026-05-22:
 *
 *   The backend PATCH /animals route's `desiredKeys` allowlist
 *   (animalController.js:113-145) does NOT include the following Phase 6 Croatian
 *   fields. The const below is the SOURCE OF TRUTH for the UI's
 *   `ANIMAL.SCAN_PASSPORT.WARN.UPDATE_PARTIAL` snackbar that surfaces AFTER a
 *   PATCH when any of these fields had values that were silently dropped.
 *
 *   Follow-up `BACKEND-PATCH-CROATIAN-FIELDS` is logged in REQUIREMENTS.md
 *   "Future Requirements (v1.2+)" — add these to the backend allowlist so the
 *   Update-existing flow can persist Croatian-field edits.
 */

// 2026-05-22 — synced with backend after Croatian semantics alignment:
// `majka_hb` was RENAMED to `zivotni_broj_majke` (now IN PATCH allowlist);
// `birth_ikg` was DROPPED (column gone, mapper no longer emits). Only
// `country_of_origin` remains missing from the backend PATCH allowlist
// (BACKEND-PATCH-CROATIAN-FIELDS follow-up).
export const MISSING_FROM_PATCH_ALLOWLIST: ReadonlyArray<string> = [
  'country_of_origin',
];

// Fields that go into the create payload (LD-5 mapped fields + form-editable fields).
const CREATE_FIELDS: ReadonlyArray<string> = [
  'name',
  'zivotni_broj',
  'farmski_broj',
  'birth_date',
  'putni_list',
  'otac_hb',
  'sire',
  'zivotni_broj_majke',
  'dam',
  'country_of_origin',
  'default_breed_id',
  'custom_breed_id',
  'default_type_id',
  'custom_type_id',
  'sex_id',
  'sex_detail',
  'origin_id',
  'identifier',
  'identifier_color_id',
  'identifier_placement_id',
  'identifier_type_id',
  'identifier_type_other',
  'brought_in_date',
  'weaning_date',
  'notes',
  'photo_url',
  'organic_status',
  'supplier',
  'price',
  'animal_use_relationships',
  'location_id', // UI-05 required-but-not-extractable (animal.location_id column)
  'eposjednik_id',
];

// PATCH /animals allowlist mirrors the backend desiredKeys at
// animalController.js:113-145. EXCLUDES `default_type_id` / `custom_type_id` —
// per the controller comment, type changes are explicitly out-of-scope for
// edit (V-PATCH-FIELDS confirmed 2026-05-22). ALSO EXCLUDES the Croatian fields
// in MISSING_FROM_PATCH_ALLOWLIST so we don't send fields the backend will
// silently strip (saga surfaces UPDATE_PARTIAL snackbar instead).
const UPDATE_FIELDS: ReadonlyArray<string> = CREATE_FIELDS.filter(
  (f) =>
    !['default_type_id', 'custom_type_id'].includes(f) &&
    !MISSING_FROM_PATCH_ALLOWLIST.includes(f),
);

function mergeEntityWithFormValues(entity: Record<string, any> | null | undefined) {
  const e = entity || {};
  const fv = (e.formValues && typeof e.formValues === 'object') ? e.formValues : {};
  return { ...e, ...fv };
}

// Backend animalModel.jsonSchema requires date-time (ISO 8601) format for
// birth_date + brought_in_date, but HTML <input type="date"> returns
// YYYY-MM-DD. Coerce by appending UTC midnight so AJV's date-time validator
// accepts it. Idempotent: already-ISO values pass through unchanged.
const DATE_FIELDS_TO_NORMALIZE = ['birth_date', 'brought_in_date', 'weaning_date'];
function normalizeDateFields(payload: Record<string, any>): Record<string, any> {
  const out = { ...payload };
  for (const f of DATE_FIELDS_TO_NORMALIZE) {
    const v = out[f];
    if (v && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      out[f] = `${v}T00:00:00.000Z`;
    }
  }
  return out;
}

/**
 * Build the POST /animals payload entry for a single review page entity.
 * UI-internal + underscore-prefixed fields are NOT copied — the result is
 * safe to JSON.stringify and send to the backend.
 */
export function buildAnimalCreatePayload(entity: Record<string, any>) {
  const merged = mergeEntityWithFormValues(entity);
  const payload: Record<string, any> = {};
  for (const f of CREATE_FIELDS) {
    if (merged[f] !== undefined) payload[f] = merged[f];
  }
  return normalizeDateFields(payload);
}

/**
 * Build the PATCH /animals payload entry for a single review page entity in
 * mode='update'. Requires `entity.target_animal_id` (set by the
 * `pageUpdateExisting` reducer when the user clicks Update existing on a
 * duplicate card).
 *
 * The payload OMITS keys in MISSING_FROM_PATCH_ALLOWLIST — those values are
 * NOT sent to the backend (which would silently drop them anyway). The saga
 * detects when those keys held non-empty values and surfaces a warning
 * snackbar with ANIMAL.SCAN_PASSPORT.WARN.UPDATE_PARTIAL.
 */
export function buildAnimalUpdatePayload(entity: Record<string, any>) {
  const merged = mergeEntityWithFormValues(entity);
  const payload: Record<string, any> = { id: entity?.target_animal_id };
  for (const f of UPDATE_FIELDS) {
    if (merged[f] !== undefined) payload[f] = merged[f];
  }
  return normalizeDateFields(payload);
}

/**
 * Returns the subset of MISSING_FROM_PATCH_ALLOWLIST fields for which the
 * (merged entity + formValues) view has a non-null/non-empty value. Used by
 * the saga to decide whether to surface UPDATE_PARTIAL after a PATCH.
 */
export function detectSilentlyDroppedFields(entity: Record<string, any>): string[] {
  const merged = mergeEntityWithFormValues(entity);
  return MISSING_FROM_PATCH_ALLOWLIST.filter((f) => {
    const v = merged[f];
    return v !== null && v !== undefined && v !== '';
  });
}
